import { prefersReducedMotion } from '@theme/utilities';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const toUnitInterval = (value, start, end) => clamp((value - start) / Math.max(end - start, 0.0001), 0, 1);
const smoothstep = (value) => value * value * (3 - 2 * value);
const READING_CHAR_CLASS = 'ch-process-story-scroll__char';
const READING_WORD_CLASS = 'ch-process-story-scroll__word';
const ACCENT_CLASS = 'ch-process-story-scroll__accent-phrase';
const EDITOR_STEP_STORAGE_PREFIX = 'ch-process-story-editor-step';

function getEditorStepStorageKey(section) {
  const sectionId = section.dataset.processStorySectionId;
  return sectionId ? `${EDITOR_STEP_STORAGE_PREFIX}:${sectionId}` : '';
}

function readEditorStep(section) {
  if (!window.Shopify?.designMode) return null;

  const storageKey = getEditorStepStorageKey(section);
  if (!storageKey) return null;

  try {
    const value = parseInt(sessionStorage.getItem(storageKey) || '', 10);
    return Number.isFinite(value) ? value : null;
  } catch {
    return null;
  }
}

function saveEditorStep(section, index) {
  if (!window.Shopify?.designMode) return;

  const storageKey = getEditorStepStorageKey(section);
  if (!storageKey) return;

  try {
    sessionStorage.setItem(storageKey, String(index));
  } catch {
    // The editor preview can still switch steps when session storage is unavailable.
  }
}

function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function accentifyTextNode(node, matcher) {
  const text = node.textContent ?? '';

  if (!text.trim()) return;

  matcher.lastIndex = 0;
  let match = matcher.exec(text);

  if (!match) return;

  const fragment = document.createDocumentFragment();
  let lastIndex = 0;

  while (match) {
    const [matchedText] = match;
    const matchIndex = match.index;

    if (matchIndex > lastIndex) {
      fragment.append(document.createTextNode(text.slice(lastIndex, matchIndex)));
    }

    const accent = document.createElement('span');
    accent.className = ACCENT_CLASS;
    accent.textContent = matchedText;
    fragment.append(accent);

    lastIndex = matchIndex + matchedText.length;
    match = matcher.exec(text);
  }

  if (lastIndex < text.length) {
    fragment.append(document.createTextNode(text.slice(lastIndex)));
  }

  node.replaceWith(fragment);
}

function applyAccentPhrases(target) {
  if (!(target instanceof Element) || target.dataset.accentPrepared === 'true') return;

  const phrases = (target.getAttribute('data-accent-phrases') || '')
    .split(/\s*,\s*|\r?\n/)
    .map((phrase) => phrase.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  if (!phrases.length) {
    target.dataset.accentPrepared = 'true';
    return;
  }

  const matcher = new RegExp(`(${phrases.map(escapeRegExp).join('|')})`, 'gi');

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      accentifyTextNode(node, matcher);
      return;
    }

    if (!(node instanceof Element)) return;
    if (node.classList.contains(ACCENT_CLASS)) return;

    [...node.childNodes].forEach((child) => walk(child));
  };

  walk(target);
  target.dataset.accentPrepared = 'true';
}

function wrapReadingCharacters(node, charBucket) {
  if (node.nodeType === Node.TEXT_NODE) {
    const text = node.textContent ?? '';
    const fragment = document.createDocumentFragment();

    text.split(/(\s+)/).forEach((token) => {
      if (!token) return;

      if (/^\s+$/.test(token)) {
        fragment.append(document.createTextNode(token));
        return;
      }

      const word = document.createElement('span');
      word.className = READING_WORD_CLASS;

      [...token].forEach((character) => {
        const span = document.createElement('span');
        span.className = READING_CHAR_CLASS;
        span.textContent = character;
        span.style.opacity = '0.3';
        word.append(span);
        charBucket.push(span);
      });

      fragment.append(word);
    });

    node.replaceWith(fragment);
    return;
  }

  if (!(node instanceof Element)) return;
  if (node.dataset.readingPrepared === 'true') return;

  [...node.childNodes].forEach((child) => wrapReadingCharacters(child, charBucket));
}

function prepareReadingContent(step) {
  const enableTextSplit = step.closest('[data-ch-process-story]')?.dataset.processStoryTextSplit === 'true';
  const content = {
    characters: [],
    targets: [...step.querySelectorAll('[data-reading-text]')],
  };

  content.targets.forEach((target) => {
    applyAccentPhrases(target);

    if (!enableTextSplit) {
      target.dataset.readingPrepared = 'true';
      return;
    }

    if (target.dataset.readingPrepared === 'true') {
      content.characters.push(...target.querySelectorAll(`.${READING_CHAR_CLASS}`));
      return;
    }

    wrapReadingCharacters(target, content.characters);
    target.dataset.readingPrepared = 'true';
  });

  return content;
}

function prepareStaticAccents(root) {
  root.querySelectorAll('[data-reading-text]').forEach((target) => {
    applyAccentPhrases(target);
  });
}

function renderReadingCharacters(characters, progress) {
  if (!characters.length) return;

  const trail = 7;
  const revealCount = progress * (characters.length + trail);

  characters.forEach((character, index) => {
    const amount = clamp((revealCount - index) / trail, 0, 1);
    const opacity = 0.3 + 0.7 * smoothstep(amount);
    character.style.opacity = opacity.toFixed(3);
  });
}

function renderReadingState(content, progress) {
  renderReadingCharacters(content.characters, progress);
}

function pauseStepVideos(node) {
  node.querySelectorAll('video').forEach((video) => {
    video.pause?.();
  });
}

function loadStepVideos(node) {
  node.querySelectorAll('video[data-process-story-video]').forEach((video) => {
    if (video.dataset.processStoryVideoLoaded === 'true') return;

    let hasDeferredSource = false;

    video.querySelectorAll('source[data-process-story-video-src]').forEach((source) => {
      const sourceUrl = source.dataset.processStoryVideoSrc;

      if (!sourceUrl) return;

      source.setAttribute('src', sourceUrl);
      hasDeferredSource = true;
    });

    if (!hasDeferredSource) return;

    video.load?.();
    video.dataset.processStoryVideoLoaded = 'true';
  });
}

function playStepVideos(node) {
  loadStepVideos(node);

  node.querySelectorAll('video').forEach((video) => {
    video.muted = true;
    video.playsInline = true;

    const playAttempt = video.play?.();
    if (playAttempt?.catch) {
      playAttempt.catch(() => {});
    }
  });
}

function syncVideos(controller, activeIndex) {
  if (controller.activeIndex === activeIndex) return;

  controller.activeIndex = activeIndex;

  controller.panels.forEach((panel, index) => {
    if (index === activeIndex) {
      playStepVideos(panel);
    } else {
      pauseStepVideos(panel);
    }
  });
}

function pauseAllVideos(controller) {
  controller.activeIndex = -1;
  controller.panels.forEach((panel) => pauseStepVideos(panel));
}

function createController(section) {
  prepareStaticAccents(section);

  const steps = [...section.querySelectorAll('.ch-process-story-scroll__desktop-step-item[data-process-story-step]')];
  const panels = [...section.querySelectorAll('.ch-process-story-scroll__media-panel[data-process-story-panel]')];
  const decorPanels = [...section.querySelectorAll('.ch-process-story-scroll__desktop-decor-panel[data-process-story-decor-panel]')];
  const stepButtons = [...section.querySelectorAll('[data-process-story-step-button]')];

  if (!steps.length || !panels.length || panels.length !== steps.length || decorPanels.length !== steps.length) {
    return null;
  }

  return {
    section,
    steps,
    panels,
    decorPanels,
    stepButtons,
    readingContent: steps.map((step) => prepareReadingContent(step)),
    readingDurations: steps.map((step) => {
      const value = parseInt(step.dataset.processStoryReadingDuration || '6000', 10);
      return Number.isFinite(value) ? Math.max(value, 1500) : 6000;
    }),
    durations: steps.map((step, index) => {
      const value = parseInt(step.dataset.processStoryDuration || '9000', 10);
      const fallback = Number.isFinite(value) ? value : 9000;
      const minDuration = Math.max((parseInt(step.dataset.processStoryReadingDuration || '6000', 10) || 6000) + 1000, 3000);
      return Math.max(fallback, minDuration, 3000);
    }),
    activeIndex: -1,
    desktopMediaQuery: window.matchMedia('(min-width: 990px)'),
    isReducedMotion: prefersReducedMotion(),
    currentIndex: 0,
    elapsed: 0,
    lastTick: 0,
    isInView: false,
    observer: null,
    rafId: 0,
    editorPinnedIndex: null,
  };
}

function applyDesktopState(controller, progress = 0, freezeReading = false, syncMedia = true) {
  const { section, steps, panels, decorPanels, stepButtons, readingContent, currentIndex, isReducedMotion, elapsed, readingDurations } = controller;
  const readingDuration = readingDurations[currentIndex] || 6000;
  const readingTimelineProgress = isReducedMotion ? 1 : clamp(elapsed / Math.max(readingDuration, 1), 0, 1);
  const readingProgress = isReducedMotion ? 1 : smoothstep(toUnitInterval(readingTimelineProgress, 0.02, 0.98));

  steps.forEach((step, index) => {
    const state = index < currentIndex ? 'past' : index === currentIndex ? 'current' : 'future';
    const isActive = index === currentIndex;
    const stepProgress = index < currentIndex ? 1 : isActive ? progress : 0;

    step.dataset.state = state;
    step.dataset.active = isActive ? 'true' : 'false';
    step.setAttribute('aria-current', isActive ? 'step' : 'false');
    step.style.setProperty('--step-progress', stepProgress.toFixed(4));

    panels[index].dataset.active = isActive ? 'true' : 'false';
    panels[index].setAttribute('aria-hidden', isActive ? 'false' : 'true');
    decorPanels[index].dataset.active = isActive ? 'true' : 'false';

    renderReadingState(readingContent[index], freezeReading || !isActive ? 1 : readingProgress);
  });

  stepButtons.forEach((button, index) => {
    button.setAttribute('aria-current', index === currentIndex ? 'step' : 'false');
  });

  section.dataset.processStoryMode = 'desktop';
  section.dataset.processStoryReady = 'true';
  if (syncMedia) {
    syncVideos(controller, currentIndex);
  }
}

function applyStaticState(controller, syncMedia = true) {
  controller.currentIndex = clamp(controller.currentIndex, 0, controller.steps.length - 1);
  controller.elapsed = 0;
  controller.lastTick = 0;
  applyDesktopState(controller, 1, true, syncMedia);
}

function resetToStart(controller, progress = 0, syncMedia = true) {
  controller.currentIndex = 0;
  controller.elapsed = 0;
  controller.lastTick = 0;
  applyDesktopState(controller, progress, false, syncMedia);
}

function renderController(controller, now = performance.now()) {
  const { section, steps, isReducedMotion } = controller;

  if (!section.isConnected) return;

  const hasPinnedEditorStep = window.Shopify?.designMode && controller.editorPinnedIndex !== null;
  if (hasPinnedEditorStep) {
    controller.currentIndex = clamp(controller.editorPinnedIndex, 0, steps.length - 1);
  }

  if (hasPinnedEditorStep) {
    applyStaticState(controller);
    return;
  }

  if (steps.length <= 1 || isReducedMotion) {
    applyStaticState(controller);
    return;
  }

  if (!controller.isInView) {
    resetToStart(controller, 0, false);
    pauseAllVideos(controller);
    return;
  }

  if (!controller.lastTick) {
    controller.lastTick = now;
  }

  const delta = now - controller.lastTick;
  controller.lastTick = now;

  if (!document.hidden) {
    controller.elapsed += delta;
  }

  let currentDuration = controller.durations[controller.currentIndex] || 5000;

  while (controller.elapsed >= currentDuration) {
    controller.elapsed -= currentDuration;
    controller.currentIndex = (controller.currentIndex + 1) % steps.length;
    currentDuration = controller.durations[controller.currentIndex] || 5000;
  }

  const progress = clamp(controller.elapsed / Math.max(currentDuration, 1), 0, 1);
  applyDesktopState(controller, progress);
}

function requestRender(controller) {
  if (controller.rafId) return;

  controller.rafId = window.requestAnimationFrame(() => {
    controller.rafId = 0;
    renderController(controller);
    if (
      controller.section.isConnected
      && controller.isInView
      && !(window.Shopify?.designMode && controller.editorPinnedIndex !== null)
    ) {
      requestRender(controller);
    }
  });
}

function bindController(controller) {
  const swipeTarget = controller.section.querySelector('.ch-process-story-scroll__desktop-visual-stage');
  let swipePointerId = null;
  let swipeStartX = 0;
  let swipeStartY = 0;

  const goToStep = (index) => {
    if (!controller.steps[index]) return;

    controller.currentIndex = index;
    controller.elapsed = 0;
    controller.lastTick = 0;
    renderController(controller);
  };
  controller.goToEditorStep = (index) => {
    if (!controller.steps[index]) return;

    controller.editorPinnedIndex = index;
    goToStep(index);
  };
  const onStepClick = (event) => {
    const step = event.currentTarget;
    if (!(step instanceof HTMLElement)) return;
    if (event.target instanceof Element && event.target.closest('a, button, input, select, textarea, summary')) return;

    const nextIndex = parseInt(step.dataset.processStoryStep || '0', 10);
    goToStep(nextIndex);
  };
  const onStepKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();

    const step = event.currentTarget;
    if (!(step instanceof HTMLElement)) return;

    const nextIndex = parseInt(step.dataset.processStoryStep || '0', 10);
    goToStep(nextIndex);
  };
  const onStepButtonClick = (event) => {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;

    const nextIndex = parseInt(button.dataset.processStoryStepButton || '0', 10);
    goToStep(nextIndex);
  };
  const onSwipeStart = (event) => {
    if (event.pointerType === 'mouse' || !event.isPrimary || controller.steps.length <= 1) return;

    swipePointerId = event.pointerId;
    swipeStartX = event.clientX;
    swipeStartY = event.clientY;
    event.currentTarget?.setPointerCapture?.(event.pointerId);
  };
  const onSwipeCancel = (event) => {
    if (event && swipePointerId === event.pointerId) {
      event.currentTarget?.releasePointerCapture?.(event.pointerId);
    }

    swipePointerId = null;
    swipeStartX = 0;
    swipeStartY = 0;
  };
  const onSwipeEnd = (event) => {
    if (swipePointerId !== event.pointerId) return;

    const deltaX = event.clientX - swipeStartX;
    const deltaY = event.clientY - swipeStartY;
    onSwipeCancel(event);

    if (Math.abs(deltaX) < 48 || Math.abs(deltaX) < Math.abs(deltaY) * 1.25) return;

    const direction = deltaX < 0 ? 1 : -1;
    goToStep((controller.currentIndex + direction + controller.steps.length) % controller.steps.length);
  };
  const onResize = () => {
    controller.lastTick = 0;
    requestRender(controller);
  };
  const onMediaQueryChange = () => {
    controller.lastTick = 0;
    requestRender(controller);
  };
  const onVisibilityChange = () => {
    controller.lastTick = 0;
  };
  const onIntersect = (entries) => {
    const entry = entries[0];
    if (!entry) return;

    const nextIsInView = entry.isIntersecting && entry.intersectionRatio >= 0.35;
    if (nextIsInView === controller.isInView) return;

    controller.isInView = nextIsInView;
    controller.lastTick = 0;

    if (controller.isInView) {
      resetToStart(controller, 0);
      requestRender(controller);
    } else {
      resetToStart(controller, 0);
      pauseAllVideos(controller);
    }
  };

  controller.cleanup = () => {
    if (controller.rafId) {
      window.cancelAnimationFrame(controller.rafId);
      controller.rafId = 0;
    }

    window.removeEventListener('resize', onResize);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    controller.steps.forEach((step) => {
      step.removeEventListener('click', onStepClick);
      step.removeEventListener('keydown', onStepKeydown);
    });
    controller.stepButtons.forEach((button) => {
      button.removeEventListener('click', onStepButtonClick);
    });
    swipeTarget?.removeEventListener('pointerdown', onSwipeStart);
    swipeTarget?.removeEventListener('pointerup', onSwipeEnd);
    swipeTarget?.removeEventListener('pointercancel', onSwipeCancel);
    controller.observer?.disconnect();
    controller.observer = null;

    if (controller.desktopMediaQuery.removeEventListener) {
      controller.desktopMediaQuery.removeEventListener('change', onMediaQueryChange);
    } else {
      controller.desktopMediaQuery.removeListener(onMediaQueryChange);
    }

    pauseAllVideos(controller);
  };

  window.addEventListener('resize', onResize);
  document.addEventListener('visibilitychange', onVisibilityChange);
  controller.steps.forEach((step) => {
    step.addEventListener('click', onStepClick);
    step.addEventListener('keydown', onStepKeydown);
  });
  controller.stepButtons.forEach((button) => {
    button.addEventListener('click', onStepButtonClick);
  });
  swipeTarget?.addEventListener('pointerdown', onSwipeStart, { passive: true });
  swipeTarget?.addEventListener('pointerup', onSwipeEnd);
  swipeTarget?.addEventListener('pointercancel', onSwipeCancel);
  if ('IntersectionObserver' in window) {
    controller.observer = new IntersectionObserver(onIntersect, {
      threshold: [0.35, 0.55],
    });
    controller.observer.observe(controller.section);
  } else {
    controller.isInView = true;
  }

  if (controller.desktopMediaQuery.addEventListener) {
    controller.desktopMediaQuery.addEventListener('change', onMediaQueryChange);
  } else {
    controller.desktopMediaQuery.addListener(onMediaQueryChange);
  }

  controller.section._chProcessStoryCleanup = controller.cleanup;
  controller.section._chProcessStoryController = controller;
  controller.lastTick = 0;
  resetToStart(controller, 0, false);
  renderController(controller);
  if (controller.isInView) {
    requestRender(controller);
  }
}

function initSections(root = document) {
  const sections = root.querySelectorAll('[data-ch-process-story]:not([data-ch-process-story-init])');

  sections.forEach((section) => {
    const controller = createController(section);
    section.dataset.chProcessStoryInit = 'true';
    if (!controller) return;

    bindController(controller);
    const savedEditorStep = readEditorStep(section);
    if (savedEditorStep !== null) {
      showEditorStep(section, savedEditorStep, false);
    }
  });
}

function showEditorStep(section, index, persist = true) {
  if (!window.Shopify?.designMode || !Number.isFinite(index)) return;

  const controller = section._chProcessStoryController;
  controller?.goToEditorStep?.(index);

  if (persist) {
    saveEditorStep(section, index);
  }
}

initSections();

document.addEventListener('shopify:section:load', (event) => {
  if (!(event.target instanceof Element)) return;
  initSections(event.target);
});

document.addEventListener('shopify:block:select', (event) => {
  if (!window.Shopify?.designMode || !(event.target instanceof Element)) return;

  const editorTarget = event.target.closest('[data-process-story-editor-step], [data-process-story-step]');
  const section = event.target.closest('[data-ch-process-story]');
  if (!editorTarget || !section) return;

  const value = editorTarget.getAttribute('data-process-story-editor-step')
    ?? editorTarget.getAttribute('data-process-story-step');
  const index = parseInt(value || '', 10);
  if (!Number.isFinite(index)) return;

  showEditorStep(section, index);
});

document.addEventListener('shopify:section:unload', (event) => {
  if (!(event.target instanceof Element)) return;

  const section = event.target.matches('[data-ch-process-story]')
    ? event.target
    : event.target.querySelector('[data-ch-process-story]');

  section?._chProcessStoryCleanup?.();
});
